import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ClipboardCheck,
    Clock,
    User,
    MapPin,
    ArrowRight,
    Search,
    Filter,
    MessageSquare,
    Eye,
    TrendingUp,
    PlayCircle,
    UserPlus,
    CheckCircle,
    AlertTriangle,
    Hash
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { useInfiniteTickets } from '../../hooks/useInfiniteTickets';

const StaffTicketTriagem = () => {
    const { tickets, claimTicket } = useTickets();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [claimingId, setClaimingId] = useState(null);


    // Hook de Paginação
    const {
        tickets: triagemTickets,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        refresh
    } = useInfiniteTickets({
        queryBuilder: (query) => {
            // Filtro Base: Status de verificação ou aguardando_sistema
            query = query.or('status.eq.verificacao,status.eq.aguardando_sistema');

            // Busca
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                // Supabase simple search (limitado, mas funcional para paginação básica)
                // Note: 'or' with references is tricky. Ideally use a search RPC or exact matches for IDs.
                // For now, let's try a broad OR filter compatible with current implementation
                query = query.or(`title.ilike.%${term}%,id.ilike.%${term}%,author_name.ilike.%${term}%`);
            }

            // Ordenação: Mais antigos primeiro
            return query.order('created_at', { ascending: true });
        },
        pageSize: 20
    }, [searchTerm]); // Recarrega se a busca mudar

    // Auto-refresh a cada 20 segundos
    useEffect(() => {
        const interval = setInterval(() => {
            refresh();
        }, 20000);
        return () => clearInterval(interval);
    }, [refresh]);



    // Categorias disponíveis (Sincronizadas com TicketList/Context)
    const categories = [
        { id: 'suporte_duvidas', label: 'Dúvidas Gerais', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
        { id: 'compras_doacoes', label: 'Compras & Doações', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
        { id: 'cupons_descontos', label: 'Cupons & Descontos', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
        { id: 'denuncia_jogador', label: 'Denúncia Jogador', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
        { id: 'contestar_punicao', label: 'Revogar Punição', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
        { id: 'programacao', label: 'Problemas Técnicos', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
        { id: 'denuncia_staff', label: 'Denúncia Staff', color: 'bg-rose-500/20 text-rose-400 border-rose-500/30' }
    ];

    const getCategoryDetails = (categoryId) => {
        if (categoryId === 'ticket_automatico') return { label: 'Automático', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
        const category = categories.find(c => c.id === categoryId);
        return category || { label: categoryId, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
    };

    const getStatusBadgeColor = (status) => {
        const colors = {
            'aberto': 'bg-green-500/20 text-green-400 border-green-500/30',
            'aguardando_sistema': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
            'fechado': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            'aguardando_usuario': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'aguardando_staff': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            'em_andamento': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
            'escalado': 'bg-red-500/20 text-red-400 border-red-500/30',
            'resolvido': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            'controle_qualidade': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            'verificacao': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
        };
        return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    };

    const getStatusLabel = (status) => {
        const labels = {
            'aberto': 'Aberto',
            'aguardando_sistema': 'Verificando',
            'fechado': 'Resolvido',
            'aguardando_usuario': 'Aguard. Você',
            'aguardando_staff': 'Aguard. Staff',
            'em_andamento': 'Em Andamento',
            'escalado': 'Escalado',
            'resolvido': 'Resolvido',
            'controle_qualidade': 'C. Qualidade',
            'verificacao': 'Triagem'
        };
        return labels[status] || status.replace(/_/g, ' ');
    };

    const getPriorityBadgeColor = (priority) => {
        const colors = {
            'alta': 'bg-red-500/20 text-red-400 border-red-500/30',
            'media': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'baixa': 'bg-green-500/20 text-green-400 border-green-500/30'
        };
        return colors[priority] || colors['media'];
    };

    const formatRelativeTime = (dateString, addSuffix = true) => {
        if (!dateString) return 'Data desconhecida';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Data inválida';

        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));

        if (diffInMinutes < 1) return 'Agora';
        if (diffInMinutes < 60) return `${diffInMinutes}min${addSuffix ? ' atrás' : ''}`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h${addSuffix ? ' atrás' : ''}`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d${addSuffix ? ' atrás' : ''}`;

        const diffInWeeks = Math.floor(diffInDays / 7);
        return `${diffInWeeks}sem${addSuffix ? ' atrás' : ''}`;
    };

    const handleQuickClaim = async (e, ticketId) => {
        e.stopPropagation(); // Impede a abertura do ticket
        setClaimingId(ticketId);
        try {
            const result = await claimTicket(ticketId);
            if (result.success) {
                toast.success('Ticket assumido para triagem!');
                navigate(`/staff/tickets/${ticketId}`);
            } else {
                toast.error('Erro ao assumir ticket: ' + result.error);
            }
        } catch (error) {
            console.error(error);
            toast.error('Erro ao processar solicitação.');
        } finally {
            setClaimingId(null);
        }
    };


    // Logic moved to useInfiniteTickets hook


    const TicketCard = ({ ticket }) => {
        const categoryDetails = getCategoryDetails(ticket.category);
        const isClaiming = claimingId === ticket.id;
        const displayContent = ticket.description || (ticket.responses?.[0]?.content) || 'Sem descrição informada.';
        const responseCount = ticket.responses ? ticket.responses.length : 0;
        const isUnassigned = !ticket.assignedTo;

        return (
            <motion.div
                whileHover={{ scale: 1.01, y: -2 }}
                className={`group relative overflow-hidden rounded-2xl border transition-all duration-300 bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.05]`}
                onClick={() => navigate(`/staff/tickets/${ticket.id}`)}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="flex items-center space-x-3">
                            <div className="flex flex-col">
                                <span className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-0.5">Ticket ID</span>
                                <span className="text-white font-mono text-sm font-bold">#{ticket.id}</span>
                            </div>
                            <div className="h-8 w-[1px] bg-white/10 mx-1" />
                            <Badge className={`${categoryDetails.color} border-none px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-md`}>
                                {categoryDetails.label}
                            </Badge>

                            {ticket.city && (
                                <Badge variant="outline" className="ml-1 border-gray-700 text-gray-400 bg-gray-900/50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {ticket.city}
                                </Badge>
                            )}
                        </div>

                        <div className="flex items-center space-x-3">
                            <div className={`flex items-center space-x-1.5 px-2 py-1 rounded-md border ${getPriorityBadgeColor(ticket.priority)}`}>
                                <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${ticket.priority === 'alta' ? 'bg-red-400' : ticket.priority === 'media' ? 'bg-yellow-400' : 'bg-green-400'}`} />
                                <span className="text-[10px] font-bold uppercase tracking-tight">{ticket.priority || 'Média'}</span>
                            </div>

                            <Badge className={`text-[10px] px-3 py-1 font-bold uppercase tracking-wider ${getStatusBadgeColor(ticket.status)} border-none rounded-full shadow-lg shadow-black/20`}>
                                {getStatusLabel(ticket.status)}
                            </Badge>
                        </div>
                    </div>

                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors duration-300 line-clamp-1 mb-1">
                            {ticket.title || 'Solicitação sem título'}
                        </h3>
                        <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">
                            {displayContent}
                        </p>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-white/5">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center text-gray-500 text-[11px]">
                                <Clock className="h-3 w-3 mr-1.5 opacity-50" />
                                <span>Criado {formatRelativeTime(ticket.created_at || ticket.createdAt)}</span>
                            </div>

                            <div className="flex items-center text-gray-500 text-[11px]">
                                <MessageSquare className="h-3 w-3 mr-1.5 opacity-50" />
                                <span>{responseCount} msg{responseCount !== 1 ? 's' : ''}</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* Botão de Ação Inteligente */}
                            {isUnassigned ? (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 px-3 text-xs uppercase font-bold tracking-wider text-green-400 hover:text-green-300 hover:bg-green-500/10 transition-colors border border-green-500/20 rounded-lg group/btn"
                                    onClick={(e) => handleQuickClaim(e, ticket.id)}
                                    disabled={isClaiming}
                                >
                                    {isClaiming ? (
                                        <span className="animate-pulse">...</span>
                                    ) : (
                                        <>
                                            <UserPlus className="w-3 h-3 mr-2 group-hover/btn:scale-110 transition-transform" />
                                            Assumir
                                        </>
                                    )}
                                </Button>
                            ) : (
                                <div className="text-[10px] text-gray-500 flex items-center">
                                    <User className="w-3 h-3 mr-1" />
                                    {ticket.assignedName || 'Em Atendimento'}
                                </div>
                            )}

                            <div className="flex items-center text-gray-400 group-hover:text-white transition-colors">
                                <Eye className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-600" />
            </motion.div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                        <ClipboardCheck className="h-8 w-8 text-blue-400" />
                        Triagem de Tickets
                    </h1>
                    <p className="text-gray-400 mt-1">Tickets aguardando verificação e classificação inicial</p>
                </div>

                <div className="relative w-full md:w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-500" />
                    </div>
                    <Input
                        type="text"
                        placeholder="Buscar na triagem..."
                        className="pl-10 bg-gray-900/50 border-gray-700 text-white focus:border-blue-500"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="grid gap-4">
                {triagemTickets.length > 0 ? (
                    <>
                        {triagemTickets.map((ticket) => (
                            <TicketCard key={ticket.id} ticket={ticket} />
                        ))}

                        {/* Sentinel / Load More Button */}
                        {hasMore && (
                            <div className="flex justify-center pt-4 pb-8">
                                <Button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    variant="outline"
                                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                                >
                                    {loadingMore ? 'Carregando...' : 'Carregar Mais Tickets'}
                                </Button>
                            </div>
                        )}
                    </>
                ) : (
                    !loading && (
                        <div className="text-center py-20 bg-gray-900/20 rounded-2xl border border-dashed border-gray-800">
                            <div className="bg-gray-800/50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <ClipboardCheck className="h-8 w-8 text-gray-600" />
                            </div>
                            <h3 className="text-xl font-medium text-white">Fila de triagem vazia</h3>
                            <p className="text-gray-400 max-w-xs mx-auto mt-2">
                                Não há tickets aguardando verificação ou processamento no momento.
                            </p>
                        </div>
                    )
                )}
                {loading && triagemTickets.length === 0 && (
                    <div className="text-center py-20">
                        <span className="text-gray-400 animate-pulse">Carregando triagem...</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StaffTicketTriagem;
